/**
 * product-faq.js
 *
 * Responsibilities:
 * 1. Tab filter — show/hide FAQ panels when a tab is clicked
 * 2. Single-open accordion — close sibling <details> when one opens
 * 3. Shopify Theme Editor awareness — re-init on section:load
 *
 * No jQuery. No global variables. Scoped per section instance.
 * Supports multiple section instances on the same page.
 */

(function () {
  'use strict';

  class ProductFaq {
    constructor(section) {
      this.section    = section;
      this.sectionId  = section.dataset.sectionId;
      this.tabs       = Array.from(section.querySelectorAll('.product-faq__tab'));
      this.panels     = Array.from(section.querySelectorAll('.product-faq__panel'));
      this.accordions = Array.from(section.querySelectorAll('[data-faq-accordion]'));

      this._bindTabs();
      this._bindAccordions();
      this._bindReadMore();
    }

    /* ── Read more: FAQs past the first few stay collapsed until asked ─── */
    _bindReadMore() {
      this.section.querySelectorAll('[data-faq-read-more]').forEach((button) => {
        button.addEventListener('click', () => {
          const panel    = button.closest('.product-faq__panel');
          const expand   = button.getAttribute('aria-expanded') !== 'true';
          panel.querySelectorAll('[data-faq-extra]').forEach((details) => {
            details.classList.toggle('product-faq__accordion--collapsed', !expand);
            if (!expand) details.removeAttribute('open');
          });
          button.setAttribute('aria-expanded', String(expand));
          button.textContent = expand ? button.dataset.labelLess : button.dataset.labelMore;
        });
      });
    }

    /* ── Tab Filter ─────────────────────────────────────────────────── */
    _bindTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', (e) => this._onTabClick(e.currentTarget));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._onTabClick(e.currentTarget);
          }
        });
      });
    }

    _onTabClick(clickedTab) {
      // Deactivate all tabs
      this.tabs.forEach((tab) => {
        tab.classList.remove('product-faq__tab--active');
        tab.setAttribute('aria-selected', 'false');

        // Remove dot from inactive tabs
        const existingDot = tab.querySelector('.product-faq__tab-dot');
        if (existingDot) existingDot.remove();
      });

      // Activate clicked tab
      clickedTab.classList.add('product-faq__tab--active');
      clickedTab.setAttribute('aria-selected', 'true');

      // Prepend dot to active tab if not already present
      if (!clickedTab.querySelector('.product-faq__tab-dot')) {
        const dot = document.createElement('span');
        dot.className    = 'product-faq__tab-dot';
        dot.setAttribute('aria-hidden', 'true');
        clickedTab.prepend(dot);
      }

      // Show matching panel, hide others
      const targetPanelId = clickedTab.getAttribute('aria-controls');
      this.panels.forEach((panel) => {
        const isTarget = panel.id === targetPanelId;
        panel.classList.toggle('product-faq__panel--hidden', !isTarget);
        if (isTarget) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
          // Close all accordions in hidden panel
          panel.querySelectorAll('[data-faq-accordion][open]').forEach((acc) => {
            acc.removeAttribute('open');
          });
        }
      });
    }

    /* ── Single-Open Accordion with Smooth Animation ─────────────────── */
    _bindAccordions() {
      this.accordions.forEach((details) => {
        const summary = details.querySelector('summary');
        if (!summary) return;

        summary.addEventListener('click', (e) => {
          e.preventDefault(); // Take full control — no native instant toggle
          if (details.open) {
            this._closeAccordion(details);
          } else {
            this._closeSiblings(details);
            this._openAccordion(details);
          }
        });
      });
    }

    _openAccordion(details) {
      if (details.dataset.animating) return;
      const content = details.querySelector('.product-faq__accordion-content');
      if (!content) { details.setAttribute('open', ''); return; }

      details.dataset.animating = 'true';
      details.setAttribute('open', '');

      const targetH = content.scrollHeight;
      content.style.height  = '0px';
      content.style.opacity = '0';
      content.offsetHeight; // force reflow — commits 0px before transition

      content.style.height  = targetH + 'px';
      content.style.opacity = '1';

      const done = () => {
        content.style.height = 'auto';
        delete details.dataset.animating;
      };
      content.addEventListener('transitionend', done, { once: true });
    }

    _closeAccordion(details) {
      if (details.dataset.animating) return;
      const content = details.querySelector('.product-faq__accordion-content');
      if (!content) { details.removeAttribute('open'); return; }

      details.dataset.animating = 'true';
      content.style.height  = content.scrollHeight + 'px';
      content.style.opacity = '1';
      content.offsetHeight; // force reflow — commits px height before transition

      content.style.height  = '0px';
      content.style.opacity = '0';

      const done = () => {
        details.removeAttribute('open');
        content.style.height  = '';
        content.style.opacity = '';
        delete details.dataset.animating;
      };
      content.addEventListener('transitionend', done, { once: true });
    }

    _closeSiblings(openedDetails) {
      const parentPanel = openedDetails.closest('.product-faq__panel');
      if (!parentPanel) return;
      parentPanel.querySelectorAll('[data-faq-accordion][open]').forEach((acc) => {
        if (acc !== openedDetails) {
          this._closeAccordion(acc);
        }
      });
    }
  }

  /* ── Bootstrap ──────────────────────────────────────────────────────────── */
  function initSection(container) {
    if (!container || container.dataset.faqInitialized) return;
    container.dataset.faqInitialized = 'true';
    new ProductFaq(container);
  }

  // Initial load
  document.querySelectorAll('.product-faq[data-section-id]').forEach((section) => {
    initSection(section);
  });

  // Shopify Theme Editor events
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.product-faq[data-section-id]');
    if (section) {
      delete section.dataset.faqInitialized;
      initSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector('.product-faq[data-section-id]');
    if (section) {
      delete section.dataset.faqInitialized;
    }
  });
})();
